import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { trackEvent } from '../../../util/datadogTracking';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import { useIntegrationActions } from '../hooks/useIntegrationActions';
import { CobaltFormValues, cobaltConfigSchema, getDefaultCobaltConfig } from './cobaltConfig.schema';
import { useCobaltConfigActions } from './useCobaltConfigActions';

export function useCobaltConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { isConnected, apiKeyLast4, isLoading, saveConfig, resetConfig } = useCobaltConfigActions();
  const { refetchIntegrations } = useIntegrationActions();

  const schema = cobaltConfigSchema(isConnected);
  const methods = useForm<CobaltFormValues>({
    resolver: yupResolver(schema as ObjectSchema<CobaltFormValues>),
    defaultValues: getDefaultCobaltConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    if (isDirty && editStatus !== 'modified') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Cobalt Config', errors);
      return;
    }
    let connected = false;
    try {
      const result = await saveConfig(getValues());
      connected = true;
      trackEvent('integration.connected', { provider: AppName.Cobalt, success: true });
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getDefaultCobaltConfig());
      await refetchIntegrations();
    } catch (error) {
      if (!connected) {
        const axiosError = error as AxiosError<{ message?: string }>;
        setErrorMessage(axiosError?.response?.data?.message || 'Failed to configure Cobalt connection');
        setSuccessMessage('');
        trackEvent('integration.connected', { provider: AppName.Cobalt, success: false });
      }
    }
  }, [trigger, saveConfig, getValues, errors, reset, refetchIntegrations]);

  const onReset = useCallback(async () => {
    await resetConfig();
    reset(getDefaultCobaltConfig());
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetConfig, reset]);

  return {
    editStatus,
    isConnected,
    apiKeyLast4,
    isLoading,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
